import { useEffect, useRef, type ReactNode } from 'react'
import { View, Animated } from 'react-native'
import { ClubLogo } from './ClubLogo'
import { SplashStyles } from '../styles/SplashStyles'

interface SplashAnimatedShellProps {
    backgroundColor: string
    children?: ReactNode
    onComplete?: () => void
    completeDelayMs?: number
    fadeOutDelayMs?: number
}

const SplashAnimatedShell = ({
    backgroundColor,
    children,
    onComplete,
    completeDelayMs = 2000,
    fadeOutDelayMs = 1500,
}: SplashAnimatedShellProps) => {
    const fadeAnim = useRef(new Animated.Value(1)).current
    const scaleAnim = useRef(new Animated.Value(0.8)).current
    const floatAnim = useRef(new Animated.Value(0)).current
    const dotAnim1 = useRef(new Animated.Value(0)).current
    const dotAnim2 = useRef(new Animated.Value(0)).current
    const dotAnim3 = useRef(new Animated.Value(0)).current

    useEffect(() => {
        Animated.parallel([
            Animated.spring(scaleAnim, {
                toValue: 1,
                useNativeDriver: true,
                tension: 50,
                friction: 7,
            }),
            Animated.loop(
                Animated.sequence([
                    Animated.timing(floatAnim, {
                        toValue: 1,
                        duration: 1500,
                        useNativeDriver: true,
                    }),
                    Animated.timing(floatAnim, {
                        toValue: 0,
                        duration: 1500,
                        useNativeDriver: true,
                    }),
                ])
            ),
        ]).start()

        const createBounceAnimation = (animValue: Animated.Value, delay: number) => {
            return Animated.loop(
                Animated.sequence([
                    Animated.delay(delay),
                    Animated.parallel([
                        Animated.timing(animValue, {
                            toValue: 1,
                            duration: 400,
                            useNativeDriver: true,
                        }),
                        Animated.timing(animValue, {
                            toValue: 0,
                            duration: 400,
                            delay: 400,
                            useNativeDriver: true,
                        }),
                    ]),
                    Animated.delay(600),
                ])
            )
        }

        createBounceAnimation(dotAnim1, 0).start()
        createBounceAnimation(dotAnim2, 160).start()
        createBounceAnimation(dotAnim3, 320).start()

        const fadeTimer = setTimeout(() => {
            Animated.timing(fadeAnim, {
                toValue: 0,
                duration: 500,
                useNativeDriver: true,
            }).start()
        }, fadeOutDelayMs)

        const completeTimer = onComplete
            ? setTimeout(onComplete, completeDelayMs)
            : undefined

        return () => {
            clearTimeout(fadeTimer)
            if (completeTimer) {
                clearTimeout(completeTimer)
            }
        }
    }, [
        fadeAnim,
        scaleAnim,
        floatAnim,
        dotAnim1,
        dotAnim2,
        dotAnim3,
        onComplete,
        completeDelayMs,
        fadeOutDelayMs,
    ])

    const floatTranslateY = floatAnim.interpolate({
        inputRange: [0, 1],
        outputRange: [0, -20],
    })

    const dot1Scale = dotAnim1.interpolate({
        inputRange: [0, 1],
        outputRange: [0, 1],
    })

    const dot2Scale = dotAnim2.interpolate({
        inputRange: [0, 1],
        outputRange: [0, 1],
    })

    const dot3Scale = dotAnim3.interpolate({
        inputRange: [0, 1],
        outputRange: [0, 1],
    })

    const dot1Opacity = dotAnim1.interpolate({
        inputRange: [0, 0.5, 1],
        outputRange: [0.5, 1, 0.5],
    })

    const dot2Opacity = dotAnim2.interpolate({
        inputRange: [0, 0.5, 1],
        outputRange: [0.5, 1, 0.5],
    })

    const dot3Opacity = dotAnim3.interpolate({
        inputRange: [0, 0.5, 1],
        outputRange: [0.5, 1, 0.5],
    })

    return (
        <Animated.View
            style={[
                SplashStyles.container,
                {
                    opacity: fadeAnim,
                    backgroundColor,
                },
            ]}
        >
            <Animated.View
                style={[
                    SplashStyles.content,
                    {
                        transform: [
                            { scale: scaleAnim },
                            { translateY: floatTranslateY },
                        ],
                    },
                ]}
            >
                <ClubLogo size={220} containerStyle={SplashStyles.logoContainer} />
                {children}
                <View style={SplashStyles.loader}>
                    <Animated.View
                        style={[
                            SplashStyles.loaderDot,
                            {
                                transform: [{ scale: dot1Scale }],
                                opacity: dot1Opacity,
                            },
                        ]}
                    />
                    <Animated.View
                        style={[
                            SplashStyles.loaderDot,
                            {
                                transform: [{ scale: dot2Scale }],
                                opacity: dot2Opacity,
                            },
                        ]}
                    />
                    <Animated.View
                        style={[
                            SplashStyles.loaderDot,
                            {
                                transform: [{ scale: dot3Scale }],
                                opacity: dot3Opacity,
                            },
                        ]}
                    />
                </View>
            </Animated.View>
        </Animated.View>
    )
}

export default SplashAnimatedShell
